import { Component, Vue } from "vue-property-decorator";
import WithRender from "./ComplaintsStatusesDetailsPage.html";
import { Inject } from "vue-di-container";
import Notifications330Service from "./Notifications330Service";
import { Notifications330FilterModel } from "./Notifications330FilterModel";
import { Toaster } from "@/Services/toast";
import {
    assignComplaintsStatusesRowKeys,
    buildNotifications330ComplaintStatusOptions,
    buildNotifications330DetailsPayload,
    buildNotifications330ScopePayload,
    createDefaultNotifications330DetailsPagination,
    downloadExcelFromBase64,
    EMPTY_GUID,
    formatCreatedDate,
    formatNotifications330ComplaintStatus,
    formatNotifications330CustomerType,
    getTodayStr,
    NOTIFICATIONS330_DASHBOARD_ROUTE,
    NOTIFICATIONS330_DETAILS_SELECT_MENU_PROPS,
    Notifications330DetailsPaginationState,
    resetNotifications330DetailsPagination,
    restoreNotifications330DetailsContext,
    saveNotifications330FilterToStorage,
} from "./notifications330DetailsShared";

@WithRender
@Component({
    diProvide: [Notifications330Service],
})
export default class ComplaintsStatusesDetailsPage extends Vue {
    @Inject(Notifications330Service)
    public notifications330Service!: Notifications330Service;

    public isLoading: boolean = false;
    public rows: any[] = [];
    public totalCount: number = 0;
    public senderNameOptions: string[] = [];
    public clientNameOptions: string[] = [];
    public senderNameFilter: string = "";
    public clientNameFilter: string = "";
    public complaintStatusFilter: number | null = null;

    private initialComplaintStatusFilter: number | null = null;

    public pagination: Notifications330DetailsPaginationState =
        createDefaultNotifications330DetailsPagination();
    public isSorted: boolean = true;
    public headers: any[] = [];

    private parentFilter: Notifications330FilterModel = {
        dateFrom: "",
        dateTo: "",
        tenant: EMPTY_GUID,
        providerId: EMPTY_GUID,
        customerType: 0,
    };

    public senderNameMenuProps = NOTIFICATIONS330_DETAILS_SELECT_MENU_PROPS;
    public clientNameMenuProps = NOTIFICATIONS330_DETAILS_SELECT_MENU_PROPS;
    public complaintStatusMenuProps = NOTIFICATIONS330_DETAILS_SELECT_MENU_PROPS;

    public get complaintStatusOptions(): Array<{ value: number; text: string }> {
        return buildNotifications330ComplaintStatusOptions({
            pendingForReview: String(this.$t("monitorDashboard.statusPendingForReview")),
            deactivated: String(this.$t("monitorDashboard.statusDeactivated")),
            falseComplaint: String(this.$t("monitorDashboard.statusFalseComplaint")),
        });
    }

    public created(): void {
        this.initHeaders();
        const restored = restoreNotifications330DetailsContext();
        if (!restored.parentFilter) {
            return;
        }
        this.parentFilter = restored.parentFilter;
        if (restored.drillDown.complaintStatus != null && restored.drillDown.complaintStatus > 0) {
            this.complaintStatusFilter = restored.drillDown.complaintStatus;
            this.initialComplaintStatusFilter = restored.drillDown.complaintStatus;
        }
    }

    public async mounted(): Promise<void> {
        if (!this.parentFilter.dateFrom) {
            this.$router.push({ name: "MonitorDashboard" });
            return;
        }
        if (!this.parentFilter.dateTo) {
            this.parentFilter.dateTo = getTodayStr();
        }
        await this.loadDropdowns();
        await this.loadTable();
    }

    public initHeaders(): void {
        this.headers = [
            {
                text: this.$t("monitorDashboard.createdDate"),
                value: "createdDate",
                align: "center",
                sortable: true,
                width: "160",
            },
            {
                text: this.$t("monitorDashboard.customerType"),
                value: "customerType",
                align: "center",
                sortable: false,
            },
            {
                text: this.$t("monitorDashboard.senderName"),
                value: "senderName",
                align: "center",
                sortable: false,
            },
            {
                text: this.$t("monitorDashboard.clientName"),
                value: "clientName",
                align: "center",
                sortable: false,
            },
            {
                text: this.$t("monitorDashboard.complaintStatus"),
                value: "complaintStatus",
                align: "center",
                sortable: false,
            },
            {
                text: this.$t("monitorDashboard.smsServiceProvider"),
                value: "smsServiceProvider",
                align: "center",
                sortable: false,
            },
            {
                text: this.$t("monitorDashboard.serviceProvider"),
                value: "serviceProvider",
                align: "center",
                sortable: false,
            },
        ];
    }

    private buildDetailsPayload(): any {
        return buildNotifications330DetailsPayload(this.parentFilter, this.pagination, {
            senderName: this.senderNameFilter,
            clientName: this.clientNameFilter,
            complaintStatus: this.complaintStatusFilter,
        });
    }

    private async loadDropdowns(): Promise<void> {
        const parentPayload = buildNotifications330ScopePayload(this.parentFilter);
        const senderResp = await this.notifications330Service.GetComplaintsStatusesSenderNames(
            parentPayload,
        );
        const clientResp = await this.notifications330Service.GetComplaintsStatusesClientNames(
            parentPayload,
        );

        this.senderNameOptions =
            senderResp && senderResp.isSuccess && Array.isArray(senderResp.result)
                ? senderResp.result
                : [];
        this.clientNameOptions =
            clientResp && clientResp.isSuccess && Array.isArray(clientResp.result)
                ? clientResp.result
                : [];
    }

    public async loadTable(): Promise<void> {
        this.isLoading = true;
        const resp = await this.notifications330Service.GetComplaintsStatusesDetails(
            this.buildDetailsPayload(),
        );
        this.isLoading = false;

        if (!resp || !resp.isSuccess) {
            if (resp && resp.errorMessage) {
                Toaster.error(String(resp.errorMessage));
            }
            this.rows = [];
            this.totalCount = 0;
            return;
        }

        this.rows = assignComplaintsStatusesRowKeys(resp.result || []);
        this.totalCount = resp.totalCount != null ? Number(resp.totalCount) : 0;
    }

    public onSenderNameChange(): void {
        this.pagination.page = 1;
        this.loadTable();
    }

    public onClientNameChange(): void {
        this.pagination.page = 1;
        this.loadTable();
    }

    public onComplaintStatusChange(): void {
        this.pagination.page = 1;
        this.loadTable();
    }

    public async resetLocalFilters(): Promise<void> {
        this.senderNameFilter = "";
        this.clientNameFilter = "";
        this.complaintStatusFilter = this.initialComplaintStatusFilter;
        resetNotifications330DetailsPagination(this.pagination);
        await this.loadTable();
    }

    public async onExport(): Promise<void> {
        const resp = await this.notifications330Service.ExportComplaintsStatusesDetails(
            this.buildDetailsPayload(),
        );
        if (!resp || !resp.isSuccess || !resp.result) {
            if (resp && resp.errorMessage) {
                Toaster.error(String(resp.errorMessage));
            }
            return;
        }
        downloadExcelFromBase64(resp.result, "ComplaintsStatusesDetails.xlsx");
    }

    public goBack(): void {
        saveNotifications330FilterToStorage(this.parentFilter);
        this.$router.push(NOTIFICATIONS330_DASHBOARD_ROUTE);
    }

    public changeSort(column: string): void {
        if (column !== "createdDate") {
            return;
        }
        if (this.pagination.sortBy === column) {
            this.pagination.descending = !this.pagination.descending;
        } else {
            this.pagination.sortBy = column;
            this.pagination.descending = true;
        }
        this.isSorted = true;
        this.loadTable();
    }

    public async updatePaginate(val: any): Promise<void> {
        this.pagination.page = val.page;
        this.pagination.rowsPerPage = val.rowsPerPage;
        await this.loadTable();
    }

    public formatCreatedDate(value: string): string {
        return formatCreatedDate(value);
    }

    public formatCustomerType(value: number | null): string {
        return formatNotifications330CustomerType(value, {
            government: String(this.$t("monitorDashboard.government")),
            charity: String(this.$t("monitorDashboard.charity")),
            privateLabel: String(this.$t("monitorDashboard.private")),
        });
    }

    public formatComplaintStatus(value: number | null): string {
        return formatNotifications330ComplaintStatus(value, {
            pendingForReview: String(this.$t("monitorDashboard.statusPendingForReview")),
            deactivated: String(this.$t("monitorDashboard.statusDeactivated")),
            falseComplaint: String(this.$t("monitorDashboard.statusFalseComplaint")),
        });
    }
}
